import { z } from 'zod'

import { Maybe } from '@/lib/types'
import { ReportingFields } from '@/travelsuit/report-settings.type'
import { UserNewZ } from '@/types/userNew'

import { AbsoluteURLZ, UserId, WithUserId } from './common'

export const CreditCardBillingDataZ = z.object({
	first_name: z.string().nullish(),
	last_name: z.string().nullish(),
	email: z.string().nullish(),
	phone_number: z.string().nullish(),
	street: z.string().nullish(),
	number: z.string().nullish(),
	postal_code: z.string().nullish(),
	city: z.string().nullish(),
	country: z.string().nullish(),
	building: z.string().nullish(),
	room: z.string().nullish(),
	floor: z.string().nullish(),
	district: z.string().nullish(),
	state: z.string().nullish(),
})

export type CreditCardBillingData = z.infer<typeof CreditCardBillingDataZ>

export const CreditCardIdZ = z.number() // TODO: restore the brand after Expense forms get sorted out .brand<'CreditCardId'>()

export type CreditCardId = z.infer<typeof CreditCardIdZ>

export const CreditCardOwnerTypeZ = z.union([z.literal('company'), z.literal('traveler')])

export type CreditCardOwnerType = z.infer<typeof CreditCardOwnerTypeZ>

// eslint-disable-next-line no-restricted-syntax
export enum UsageAllowedFor {
	Country = 'country',
	InvoiceProfile = 'invoice_profile',
}

export const UsageAllowedForZ = z.enum(UsageAllowedFor)

// eslint-disable-next-line no-restricted-syntax
export enum CreditCardStatus {
	Active = 'active',
	Draft = 'draft',
	Failed = 'failed',
	Invalid = 'invalid',
	InvalidAirPlus = 'invalid_airplus',
}

export const CreditCardStatusZ = z.enum(CreditCardStatus)

// eslint-disable-next-line no-restricted-syntax
export enum CreditCardType {
	AirPlus = 'air-plus',
	AmericanExpress = 'amex',
	AmexBTA = 'amex-bta',
	AmexBTAGermany = 'amex-bta-germany',
	Diners = 'diners',
	MasterCard = 'master-card',
	Other = 'other',
	Visa = 'visa',
}

export const CreditCardTypeZ = z.enum(CreditCardType)

export const CreditCardPermissionsZ = z.object({
	usage_allowed_for: UsageAllowedForZ.nullable(),
	available_only_for_travel_arrangers: z.boolean(),
	permitted_for_flights: z.boolean(),
	permitted_for_trains: z.boolean(),
	permitted_for_hotels: z.boolean(),
	permitted_for_cars: z.boolean(),
	permitted_for_fees: z.boolean(),
})

export type CreditCardPermissions = z.infer<typeof CreditCardPermissionsZ>

const CreditCardInvoiceProfileZ = z.object({ id: z.number(), display_name: z.string() })

export const CreditCardZ = z.object({
	card_data: CreditCardBillingDataZ.nullish(),
	card_expiration_date: z.string(),
	card_type: CreditCardTypeZ,
	id: CreditCardIdZ,
	created_dt: z.string(),
	card_holder_name: z.string(),
	card_number: z.string(),
	allow_use_my_card: z.boolean(),
	card_name: z.string(),
	related_to: CreditCardOwnerTypeZ,
	cvc_exist: z.boolean().nullable(),
	status: CreditCardStatusZ,
	used_for_package_payment: z.boolean().nullable(),
	cost_center: z.boolean().nullish(),
	employee_id: z.boolean().nullish(),
	department: z.boolean().nullish(),
	accounting_unit: z.boolean().nullish(),
	action_code: z.boolean().nullish(),
	purchase_order_number: z.boolean().nullish(),
	project_code: z.boolean().nullish(),
	default_for_fees: z.boolean().nullish(),
	invoice_profile: CreditCardInvoiceProfileZ.nullish(),
	user: UserNewZ.pick({
		id: true,
		first_name: true,
		last_name: true,
		middle_name: true,
	}).nullish(),
	...CreditCardPermissionsZ.shape,
})

export type CreditCard = z.infer<typeof CreditCardZ>

export const GetCreditCardStatusResponseZ = z.object({
	status: CreditCardStatusZ,
	worldline_verification_url: AbsoluteURLZ.nullable(),
})

export type GetCreditCardStatusResponse = z.infer<typeof GetCreditCardStatusResponseZ>

export interface WithServicesPermissions {
	permitted_for_flights: boolean
	permitted_for_trains: boolean
	permitted_for_hotels: boolean
	permitted_for_cars: boolean
	permitted_for_fees: boolean
}

export interface WithCreditCardPermissions extends WithServicesPermissions {
	usage_allowed_for: UsageAllowedFor | null
	available_only_for_travel_arrangers: boolean
}

export interface CreditCardResponse extends WithCreditCardPermissions {
	card_data?: CreditCardBillingData
	card_expiration_date: string
	created_dt: string
	card_type: CreditCardType
	id: CreditCardId
	card_holder_name: string
	card_number: string
	allow_use_my_card: boolean
	card_name: string
	related_to: CreditCardOwnerType
	cvc_exist: boolean | null
	status: CreditCardStatus
	used_for_package_payment?: boolean
	cost_center?: boolean
	employee_id?: boolean
	department?: boolean
	accounting_unit?: boolean
	action_code?: boolean
	purchase_order_number?: boolean
	project_code?: boolean
	default_for_fees?: boolean
	invoice_profile?: { id: number; display_name: string } | null
	user?: { id: UserId; first_name: string; last_name: string; middle_name: string | null } | null
}

export interface WithCreditCards<T = CreditCardResponse[]> {
	credit_cards: T
}

export interface WithCardCvc<T = string> {
	card_cvc?: T
}

export interface CreateCreditCardData extends WithCreditCardPermissions, WithUserId<Maybe<UserId>>, WithCardCvc {
	card_holder_name: string
	card_name: string
	card_number: string
	card_expiration_date: string
	card_type: CreditCardType
	card_data: CreditCardBillingData
	allow_use_my_card: boolean
	related_to: CreditCardOwnerType
	invoice_profile_id?: number | null
	temporary_card?: boolean
	cost_center?: boolean
	employee_id?: boolean
	department?: boolean
	accounting_unit?: boolean
	action_code?: boolean
	purchase_order_number?: boolean
	project_code?: boolean
	default_for_fees?: boolean
	mock: string | null
}

export interface CreditCardVerificationData {
	worldline_method_code: string
	worldline_token: string
	worldline_validated: boolean
	scheme_transaction_id: string | null
}

export interface WithCreditCardId<T = number> {
	credit_card_id: T
}

export interface CreditCardInfo {
	id: CreditCardId
	card_number: string
	created_dt: string
	card_holder_name: string
	card_type: CreditCardType
	card_expiration_date: string
	card_name: string
	allow_use_my_card?: boolean
	card_data?: CreditCardBillingData
	used_for_package_payment?: boolean
	cvc_exist: boolean | null
	reporting_fields?: ReportingFields[]
	default_for_fees?: boolean
	status: CreditCardStatus
}
